import React, { useState, useRef } from 'react';
import { Upload, FileText, X, Check, ArrowLeft, Eye, Download } from 'lucide-react';
import { imagesService } from '../services/imagesService';

const DocumentUploader = ({ onDocumentUpload, currentDocument = null, onCancel = null, type = 'entry' }) => {
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(currentDocument);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const validateFile = (file) => {
    // Validar tamaño (máximo 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      throw new Error('El archivo es demasiado grande. El tamaño máximo es 10MB.');
    }

    // Validar tipo de archivo
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf'
    ];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Tipo de archivo no soportado. Solo se permiten imágenes (JPG, PNG, GIF, WEBP) y PDF.');
    }
  };

  const getFileIcon = (filename) => {
    if (!filename) return FileText;
    const extension = filename.toLowerCase().split('.').pop();
    return extension === 'pdf' ? FileText : Eye;
  };

  const getFileType = (filename) => {
    if (!filename) return 'archivo';
    const extension = filename.toLowerCase().split('.').pop();
    return extension === 'pdf' ? 'PDF' : 'Imagen';
  };

  const handleFiles = async (files) => {
    if (files && files.length > 0) {
      const file = files[0];
      setError(null);
      
      try {
        // Validar archivo
        validateFile(file);

        setUploading(true);

        // Subir archivo al servidor
        const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
        let response;
        if (isPdf || type === 'entry') {
          console.log('➡️ Usando endpoint requisition (PDF permitido)');
          response = await imagesService.uploadRequisitionImage(file);
        } else {
          console.log('➡️ Usando endpoint imagen');
          response = await imagesService.uploadImage(file);
        }
        
        if (response.success) {
          setPreview(response.data.filename);
          onDocumentUpload(response.data.filename);
          console.log('✅ Documento subido exitosamente:', response.data.filename);
        } else {
          throw new Error(response.message || 'Error subiendo documento');
        }

      } catch (error) {
        console.error('❌ Error procesando documento:', error);
        if ((file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) && /Solo se permiten imágenes/.test(error.message)) {
          setError('El backend todavía no acepta PDF en esta ruta. Reinicia servidor y confirma /api/images/upload-requisition activo.');
        } else {
          setError(error.message);
        }
        setPreview(null);
      } finally {
        setUploading(false);
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer?.files;
    handleFiles(files);
  };

  const handleInputChange = (e) => {
    const files = e.target.files;
    handleFiles(files);
  };

  const openFileDialog = () => {
    fileInputRef.current?.click();
  };

  const removeDocument = async () => {
    try {
      if (preview) {
        await imagesService.deleteImage(preview);
        console.log('✅ Documento eliminado del servidor');
      }
    } catch (error) {
      console.error('❌ Error eliminando documento del servidor:', error);
      // Continuar con la eliminación local aunque falle la del servidor
    }
    
    setPreview(null);
    setError(null);
    onDocumentUpload(null);
  };

  const viewDocument = () => {
    if (preview) {
      const url = imagesService.getImageUrl(preview);
      window.open(url, '_blank');
    }
  };

  const downloadDocument = async () => {
    if (preview) {
      try {
        const url = imagesService.getImageUrl(preview);
        const response = await fetch(url);
        const blob = await response.blob();
        
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = preview;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(downloadUrl);
      } catch (error) {
        console.error('Error descargando documento:', error);
      }
    }
  };

  return (
    <div className="w-full">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.pdf"
        onChange={handleInputChange}
        className="hidden"
      />
      
      {/* Mostrar errores */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg">
          <p className="text-sm">{error}</p>
        </div>
      )}
      
      {preview ? (
        // Vista del documento actual
        <div className="relative bg-gray-50 rounded-lg border border-gray-300 p-6">
          <div className="flex items-center justify-center flex-col space-y-4">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              {React.createElement(getFileIcon(preview), { className: "w-8 h-8 text-blue-600" })}
            </div>
            
            <div className="text-center">
              <p className="font-medium text-gray-800">{getFileType(preview)} de Requisición</p>
              <p className="text-sm text-gray-600 break-all">{preview}</p>
            </div>
            
            {uploading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                <div className="text-white text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                  <p className="text-sm">Subiendo documento...</p>
                </div>
              </div>
            )}
            
            <div className="flex gap-2">
              <button
                onClick={viewDocument}
                disabled={uploading}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Ver documento"
              >
                <Eye className="w-4 h-4" />
                Ver
              </button>
              
              <button
                onClick={downloadDocument}
                disabled={uploading}
                className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Descargar documento"
              >
                <Download className="w-4 h-4" />
                Descargar
              </button>
              
              <button
                onClick={openFileDialog}
                disabled={uploading}
                className="flex items-center gap-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Cambiar documento"
              >
                <Upload className="w-4 h-4" />
                Cambiar
              </button>
              
              <button
                onClick={removeDocument}
                disabled={uploading}
                className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Eliminar documento"
              >
                <X className="w-4 h-4" />
                Eliminar
              </button>
              
              {onCancel && (
                <button
                  onClick={onCancel}
                  disabled={uploading}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Cancelar y cerrar"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cancelar
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        // Zona de arrastrar y soltar
        <div
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-300 cursor-pointer
            ${dragActive 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'
            }
            ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={!uploading ? openFileDialog : undefined}
        >
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <Upload className={`w-8 h-8 ${dragActive ? 'text-blue-600' : 'text-blue-500'}`} />
            </div>
            
            <div>
              <p className="text-lg font-medium text-gray-700">
                {uploading ? 'Subiendo documento...' : 
                 dragActive ? 'Suelta el documento aquí' : 
                 'Subir documento de requisición'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {uploading ? 'Por favor espera...' :
                 'Arrastra y suelta un archivo o haz clic para seleccionar'}
              </p>
              <p className="text-xs text-gray-400 mt-2">
                Formatos soportados: Imágenes (JPG, PNG, GIF, WEBP) y PDF (máx. 10MB)
              </p>
            </div>
            
            <div className="flex gap-3 justify-center">
              <button
                type="button"
                disabled={uploading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="w-4 h-4" />
                {uploading ? 'Subiendo...' : 'Seleccionar Archivo'}
              </button>
              
              {onCancel && (
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Cancelar
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUploader;
